import { ProductDetails } from "@/types/product-details";
import { useQuery } from "@tanstack/react-query";
import { Offer } from "@/types/offers";
import { cacheApiData } from "@repo/next-shared/redis/redis-cache";
import { redisKeys } from "@repo/next-shared/redis/redis-keys";

export async function getServerProductDetails(id: string) {
  return await cacheApiData(redisKeys.PRODUCT_DETAILS(id), {
    path: `/catalog/customer/product/${id}`,
    method: "GET",
    serverSideError: true,
  });
}

export async function getProductDetails(id: string) {
  return await cacheApiData(redisKeys.PRODUCT_DETAILS(id), {
    path: `/catalog/customer/product/${id}`,
    method: "GET",
  });
}

export function useProductDetailsBase(id: string, select: any): any {
  return useQuery({
    queryKey: ["product", id],
    queryFn: () => getProductDetails(id),
    select,
  });
}

//*** SELECTORS ***///
export const useProductDetails = (
  productId: string,
  offer: Offer | null = null,
) => {
  if (!!offer) {
    return useQuery({
      queryKey: ["product", productId],
      queryFn: () => offer,
      select: (offer: Offer) => ({
        title: offer.productTitle,
        brand: offer.brand,
      }),
    });
  }

  return useProductDetailsBase(productId, (product: ProductDetails) => product);
};

export const useProductBySize = (productId: string) =>
  useProductDetailsBase(productId, (product: ProductDetails) =>
    product.variants.reduce(
      (grouped: { [key: string]: Array<any> }, variant) => {
        const size = variant.size;
        if (!grouped[size]) {
          grouped[size] = [];
        }
        grouped[size].push(variant);
        return grouped;
      },
      {},
    ),
  );

export const useProductGetVariant = (
  productId: string,
  variantEan?: string,
  offer: Offer | null = null,
) => {
  if (!!offer) {
    return useQuery({
      queryKey: ["product", productId],
      queryFn: () => offer,
      select: () => ({
        color: {
          name: offer.colorName,
        },
        size: offer.size,
        normalizedSize: offer.normalizedSize,
        avgStorePrice: offer.avgStorePrice,
        catalogImgUrl: offer.catalogImgUrl,
      }),
    });
  }

  return variantEan
    ? useProductDetailsBase(productId, (product: ProductDetails) => {
        return product.variants.find((variant) => variant.ean === variantEan);
      })
    : useProductDetailsBase(productId, (product: ProductDetails) => {
        return (
          product.variants.find((variant: any) => {
            return variant.defaultVariant;
          }) || product.variants[0]
        );
      });
};

export const useProductGetColors = (productId: string) =>
  useProductDetailsBase(productId, (product: ProductDetails) => {
    return product.variants
      .map((variant) => {
        return {
          ...variant.color,
          imgUrl: /*variant.imgUrl[0] || */ variant.catalogImgUrl,
        };
      })
      .filter((value, index, self) => {
        return self.findIndex((el) => el.name === value.name) === index;
      });
  });
